/**
 * Signing in with Microsoft in the tab the portal is in: the browser goes to
 * Microsoft and comes back to the login page with the sign-in token in the
 * address. Only the sign-in token is asked for. Nothing here opens a popup or
 * a hidden frame, which is what could not hand a sign-in back on a phone or
 * in a private tab.
 */

const CLIENT_ID = '35c218ef-227d-40fe-aac6-70ff4e50191c';
const AUTHORITY = 'https://login.microsoftonline.com/dgtek.net';

/** Where a sign-in in progress is remembered, for the tab. */
export const PENDING_KEY = 'microsoft-sign-in';

/** What a sign-in was started with, which what comes back must match. */
export interface PendingSignIn {
  state: string;
  nonce: string;
}

export type SignInResult = { token: string } | { error: string };

const random = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const newPendingSignIn = (): PendingSignIn => ({ state: random(), nonce: random() });

export const authorizeUrl = (
  { redirectUri, state, nonce }: PendingSignIn & { redirectUri: string },
): string => {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'id_token',
    response_mode: 'fragment',
    scope: 'openid profile',
    redirect_uri: redirectUri,
    state,
    nonce,
  });
  return `${AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`;
};

/** Sends the tab to Microsoft. On its own so a test can stand in for it. */
export const leaveForMicrosoft = (url: string): void => {
  window.location.assign(url);
};

const nonceOf = (token: string): string | undefined => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded)).nonce;
  } catch {
    return undefined;
  }
};

/**
 * What the address says about a sign-in: the token, what went wrong, or
 * null when it carries no sign-in at all. A token is only taken when it
 * answers the sign-in this tab started (state) and was issued for it
 * (nonce). The API checks its signature and who it is for.
 */
export const readSignIn = (hash: string, pending: PendingSignIn | null): SignInResult | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('id_token');
  const error = params.get('error');
  if (!token && !error) return null;
  if (error) {
    return { error: params.get('error_description') ?? error };
  }
  if (!token || !pending || params.get('state') !== pending.state || nonceOf(token) !== pending.nonce) {
    return { error: 'The sign-in that came back is not the one this page started.' };
  }
  return { token };
};

export const readPendingSignIn = (): PendingSignIn | null => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null');
    return stored && typeof stored.state === 'string' && typeof stored.nonce === 'string' ? stored : null;
  } catch {
    return null;
  }
};
